import { useMemo } from "react";
import { Markdown } from "./Markdown";
import { EntityRef } from "./EntityRef";

/** Renders a node_notes body_markdown blob the way the human is meant
 *  to read it: YAML frontmatter parsed into a clean key/value grid
 *  (the watcher's bug was raw "---" + "memso-id: foo" lines showing
 *  through verbatim), markdown body rendered via the shared Markdown
 *  component (so [[EntityName]] backlinks become tappable EntityRef
 *  chips), and the special `related: [...]` array surfaced as a row
 *  of clickable entity chips above the body. */
export function WikiNote({ markdown }: { markdown: string }) {
  const parsed = useMemo(() => parseFrontmatter(markdown), [markdown]);
  return (
    <div className="space-y-3">
      {parsed.frontmatter && <Frontmatter fm={parsed.frontmatter} />}
      <Markdown>{parsed.body}</Markdown>
    </div>
  );
}

interface ParsedNote {
  frontmatter: Record<string, string | string[]> | null;
  body: string;
}

/** Pull the YAML frontmatter block (between leading "---" lines) off the
 *  top of a markdown string. Conservative: only recognises a frontmatter
 *  block when the very first non-empty line is "---". Each subsequent
 *  line is parsed as `key: value`; bracketed list literals
 *  `related: ["a", "b"]` become string arrays. Returns the rest of the
 *  body unchanged so the markdown renderer can do its job. */
function parseFrontmatter(raw: string): ParsedNote {
  const text = raw.replace(/^﻿/, "");
  const lines = text.split("\n");
  let i = 0;
  while (i < lines.length && lines[i].trim() === "") i++;
  if (i >= lines.length || lines[i].trim() !== "---") {
    return { frontmatter: null, body: raw };
  }
  const start = i + 1;
  let end = -1;
  for (let j = start; j < lines.length; j++) {
    if (lines[j].trim() === "---") {
      end = j;
      break;
    }
  }
  if (end < 0) return { frontmatter: null, body: raw };
  const fm: Record<string, string | string[]> = {};
  for (let j = start; j < end; j++) {
    const line = lines[j];
    const m = line.match(/^([A-Za-z][A-Za-z0-9-_]*):\s*(.*)$/);
    if (!m) continue;
    const key = m[1];
    const val = m[2].trim();
    if (val.startsWith("[") && val.endsWith("]")) {
      const inner = val.slice(1, -1);
      const items = splitListLiteral(inner)
        .map(stripQuotes)
        .filter((s) => s.length > 0);
      fm[key] = items;
    } else {
      fm[key] = stripQuotes(val);
    }
  }
  const body = lines.slice(end + 1).join("\n").trimStart();
  return { frontmatter: fm, body };
}

function splitListLiteral(inner: string): string[] {
  const out: string[] = [];
  let buf = "";
  let inStr = false;
  let strCh = "";
  for (let k = 0; k < inner.length; k++) {
    const ch = inner[k];
    if (inStr) {
      if (ch === "\\" && k + 1 < inner.length) {
        buf += ch + inner[++k];
        continue;
      }
      if (ch === strCh) {
        inStr = false;
        buf += ch;
        continue;
      }
      buf += ch;
      continue;
    }
    if (ch === '"' || ch === "'") {
      inStr = true;
      strCh = ch;
      buf += ch;
      continue;
    }
    if (ch === ",") {
      out.push(buf.trim());
      buf = "";
      continue;
    }
    buf += ch;
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

function stripQuotes(s: string): string {
  const t = s.trim();
  if (t.length < 2) return t;
  if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
    try {
      return JSON.parse('"' + t.slice(1, -1).replace(/"/g, '\\"') + '"');
    } catch {
      return t.slice(1, -1);
    }
  }
  return t;
}

/** Render parsed frontmatter as a key/value pill grid. The `related`
 *  array gets its own row of clickable EntityRef chips; `tags` becomes
 *  small slate pills; every other key renders as a single inline pair. */
function Frontmatter({ fm }: { fm: Record<string, string | string[]> }) {
  const related = Array.isArray(fm.related) ? fm.related : null;
  const tags = Array.isArray(fm.tags) ? fm.tags : null;
  const scalarKeys = Object.keys(fm).filter(
    (k) => k !== "related" && k !== "tags" && typeof fm[k] === "string",
  );
  if (scalarKeys.length === 0 && !related && !tags) return null;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/30 p-3 text-xs space-y-2">
      {scalarKeys.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
          {scalarKeys.map((k) => (
            <div key={k} className="contents">
              <dt className="font-semibold text-slate-500 dark:text-slate-400 lowercase tracking-wide">
                {k}
              </dt>
              <dd className="text-slate-800 dark:text-slate-100 break-words">
                {String(fm[k])}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {related && related.length > 0 && (
        <div>
          <div className="font-semibold text-slate-500 dark:text-slate-400 mb-1">related</div>
          <div className="flex flex-wrap gap-1.5">
            {related.map((name) => (
              <EntityRef key={name} name={name} />
            ))}
          </div>
        </div>
      )}
      {tags && tags.length > 0 && (
        <div>
          <div className="font-semibold text-slate-500 dark:text-slate-400 mb-1">tags</div>
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[10px] font-mono"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
